import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowBack,
  SubdirectoryArrowRight,
} from "@mui/icons-material";
import { Layout } from "../components/Layout";
import { invoiceService, InvoiceFull } from "../services/invoice.service";
import apiClient from "../services/api";
import { formatLocalDate } from "../utils/dateUtils";

interface AvailablePO {
  id: number;
  poNumber: string;
  poDate: string;
  statusName: string;
  comment: string;
  totalCharge: number;
  subClientName?: string;
  fromInventory: boolean;
}

const EditInvoicePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState<InvoiceFull | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [comment, setComment] = useState("");
  const [statusId, setStatusId] = useState(1); // Default to Building

  const [availablePOs, setAvailablePOs] = useState<AvailablePO[]>([]);
  const [selectedPOs, setSelectedPOs] = useState<Set<number>>(new Set());

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmNumber, setDeleteConfirmNumber] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const statuses = [
    { id: 1, name: "Building" },
    { id: 2, name: "Invoiced" },
    { id: 3, name: "Paid" },
  ];

  const loadInvoice = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const data = await invoiceService.getInvoiceById(parseInt(id));
      console.log("Invoice data loaded:", data);
      console.log("StatusId from data:", data.statusId, typeof data.statusId);
      setInvoice(data);
      setInvoiceNumber(data.invoiceNumber);
      setComment(data.comment || "");
      setStatusId((data as any).statusID || data.statusId || 1);
      console.log("StatusId state set to:", data.statusId);

      // Load available POs for this client
      const availableData = await invoiceService.getAvailablePurchaseOrders(
        data.clientID,
      );
      setAvailablePOs(availableData.purchaseOrders || []);
    } catch (err) {
      setError("Failed to load invoice");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleUpdateHeader = async () => {
    if (!id) return;

    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      await invoiceService.updateInvoice(parseInt(id), {
        invoiceNumber,
        comment,
        statusId,
      });

      setSuccessMessage("Invoice updated successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
      loadInvoice(); // Reload to get updated data
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleAddSelectedPOs = async () => {
    if (!id || selectedPOs.size === 0) return;

    setSaving(true);
    setError("");

    try {
      for (const poId of Array.from(selectedPOs)) {
        const po = availablePOs.find((p) => p.id === poId);
        if (po) {
          await invoiceService.addPurchaseOrderToInvoice(
            parseInt(id),
            poId,
            po.totalCharge,
          );
        }
      }

      setSelectedPOs(new Set());
      setSuccessMessage("Purchase orders added successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
      loadInvoice(); // Reload to refresh both lists
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to add purchase orders");
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePO = async (linkId: number) => {
    if (!id) return;

    setSaving(true);
    setError("");

    try {
      await invoiceService.removePurchaseOrderFromInvoice(parseInt(id), linkId);

      setSuccessMessage("Purchase order removed successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
      loadInvoice(); // Reload to refresh both lists
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove purchase order");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePO = (poId: number) => {
    const newSelected = new Set(selectedPOs);
    if (newSelected.has(poId)) {
      newSelected.delete(poId);
    } else {
      newSelected.add(poId);
    }
    setSelectedPOs(newSelected);
  };

  const handleSelectAllPOs = () => {
    if (selectedPOs.size === availablePOs.length) {
      setSelectedPOs(new Set());
    } else {
      setSelectedPOs(new Set(availablePOs.map((po) => po.id)));
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError("");

    try {
      const response = await apiClient.get(`/invoices/${id}/download`, {
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Invoice_${invoiceNumber}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMessage("Invoice downloaded successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      setError("Failed to download invoice");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!invoiceNumber || deleteConfirmNumber !== invoiceNumber) {
      setError("Invoice number does not match");
      return;
    }

    setDeleting(true);
    setError("");

    try {
      await invoiceService.deleteInvoice(parseInt(id!), deleteConfirmNumber);
      navigate("/invoices");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete invoice");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          minHeight='400px'
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!invoice) {
    return (
      <Layout>
        <Alert severity='error'>Invoice not found</Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Box display='flex' alignItems='center' mb={3} gap={2}>
          <IconButton onClick={() => navigate("/invoices")}>
            <ArrowBack />
          </IconButton>
          <Typography variant='h4' sx={{ flexGrow: 1 }}>
            Edit Invoice: {invoice.invoiceNumber}
          </Typography>
          <Button
            variant='outlined'
            color='error'
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete Invoice
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleDownload}
            disabled={downloading}
            startIcon={downloading ? <CircularProgress size={20} /> : null}
          >
            {downloading ? "Generating..." : "Generate Invoice Download"}
          </Button>
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity='success' sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Invoice Header */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant='h6' gutterBottom>
              Invoice Details
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                label='Invoice Number'
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                fullWidth
              />
              <TextField
                label='Client'
                value={invoice.clientName}
                fullWidth
                disabled
              />
              <TextField
                select
                label='Status'
                value={Number(statusId)}
                onChange={(e) => setStatusId(Number(e.target.value))}
                fullWidth
              >
                {statuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              label='Comment'
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              multiline
              rows={2}
              fullWidth
              sx={{ mb: 2 }}
            />

            {/* Invoice Totals Breakdown */}
            <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant='subtitle1' gutterBottom fontWeight='bold'>
                Invoice Totals
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant='body2'>Items Subtotal:</Typography>
                  <Typography variant='body2'>
                    $
                    {(
                      invoice.invoiceTotal -
                      (invoice.shippingTotal || 0) -
                      (invoice.handlingTotal || 0)
                    ).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant='body2'>Shipping:</Typography>
                  <Typography variant='body2'>
                    ${(invoice.shippingTotal || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant='body2'>Handling:</Typography>
                  <Typography variant='body2'>
                    ${(invoice.handlingTotal || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                    pt: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant='h6' fontWeight='bold'>
                    Grand Total:
                  </Typography>
                  <Typography variant='h6' fontWeight='bold'>
                    ${invoice.invoiceTotal.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box display='flex' justifyContent='flex-end' alignItems='center'>
              <Button
                variant='contained'
                onClick={handleUpdateHeader}
                disabled={saving}
              >
                {saving ? <CircularProgress size={24} /> : "Update Header"}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Current Purchase Orders on Invoice */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant='h6' gutterBottom>
              Purchase Orders on This Invoice
            </Typography>
            {invoice.purchaseOrders.length === 0 ? (
              <Alert severity='info'>
                No purchase orders added yet. Select from available POs below.
              </Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>PO Number</TableCell>
                      <TableCell>PO Date</TableCell>
                      <TableCell align='right'>Total</TableCell>
                      <TableCell align='center'>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.purchaseOrders.map((po) => (
                      <TableRow key={po.id}>
                        <TableCell>
                          <Box>
                            <Typography variant='body2'>
                              {po.poNumber}
                            </Typography>
                            {po.fromInventory && (
                              <Chip
                                label='From Inventory'
                                size='small'
                                color='info'
                                sx={{ mt: 0.5, height: 20, fontSize: '0.75rem' }}
                              />
                            )}
                            {po.subClientName && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mt: 0.5,
                                }}
                              >
                                <SubdirectoryArrowRight
                                  sx={{
                                    fontSize: 14,
                                    mr: 0.5,
                                    color: "text.secondary",
                                  }}
                                />
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                >
                                  {po.subClientName}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {formatLocalDate(po.poDate) || "-"}
                        </TableCell>
                        <TableCell align='right'>
                          ${po.purchaseOrderTotal.toFixed(2)}
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton
                            color='error'
                            onClick={() => handleRemovePO(po.id)}
                            size='small'
                            disabled={saving}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Available Purchase Orders */}
        <Card>
          <CardContent>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              mb={2}
            >
              <Typography variant='h6'>Available Purchase Orders</Typography>
              <Button
                variant='contained'
                color='primary'
                startIcon={<AddIcon />}
                onClick={handleAddSelectedPOs}
                disabled={saving || selectedPOs.size === 0}
              >
                Add {selectedPOs.size > 0 ? `${selectedPOs.size} ` : ""}
                Selected
              </Button>
            </Box>

            <Alert severity='info' sx={{ mb: 2 }}>
              <Typography variant='body2'>
                <strong>Note:</strong> "From Inventory" purchase orders are
                included as $0 charge items. These represent supplies sent from
                existing pre-purchased inventory and do not incur additional
                billing.
              </Typography>
            </Alert>

            {availablePOs.length === 0 ? (
              <Alert severity='info'>
                No available purchase orders for this client. All billable POs
                have been invoiced.
              </Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={
                            availablePOs.length > 0 &&
                            selectedPOs.size === availablePOs.length
                          }
                          indeterminate={
                            selectedPOs.size > 0 &&
                            selectedPOs.size < availablePOs.length
                          }
                          onChange={handleSelectAllPOs}
                        />
                      </TableCell>
                      <TableCell>PO Number</TableCell>
                      <TableCell>PO Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Comment</TableCell>
                      <TableCell align='right'>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availablePOs.map((po) => (
                      <TableRow
                        key={po.id}
                        hover
                        onClick={() => handleTogglePO(po.id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell padding='checkbox'>
                          <Checkbox checked={selectedPOs.has(po.id)} />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant='body2'>
                              {po.poNumber}
                            </Typography>
                            {po.fromInventory && (
                              <Chip
                                label='From Inventory'
                                size='small'
                                color='info'
                                sx={{ mt: 0.5, height: 20, fontSize: '0.75rem' }}
                              />
                            )}
                            {po.subClientName && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mt: 0.5,
                                }}
                              >
                                <SubdirectoryArrowRight
                                  sx={{
                                    fontSize: 14,
                                    mr: 0.5,
                                    color: "text.secondary",
                                  }}
                                />
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                >
                                  {po.subClientName}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{formatLocalDate(po.poDate)}</TableCell>
                        <TableCell>
                          <Chip label={po.statusName} size='small' />
                        </TableCell>
                        <TableCell>{po.comment || "-"}</TableCell>
                        <TableCell align='right'>
                          ${po.totalCharge.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={() => !deleting && setDeleteModalOpen(false)}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>Delete Invoice</DialogTitle>
          <DialogContent>
            <Alert severity='warning' sx={{ mb: 2 }}>
              This will permanently delete the invoice and mark all associated
              purchase orders as not invoiced.
            </Alert>
            <Typography variant='body2' gutterBottom>
              To confirm deletion, please type the invoice number:{" "}
              <strong>{invoiceNumber}</strong>
            </Typography>
            <TextField
              fullWidth
              label='Invoice Number'
              value={deleteConfirmNumber}
              onChange={(e) => setDeleteConfirmNumber(e.target.value)}
              disabled={deleting}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeleteModalOpen(false);
                setDeleteConfirmNumber("");
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              color='error'
              variant='contained'
              disabled={deleting || deleteConfirmNumber !== invoiceNumber}
            >
              {deleting ? "Deleting..." : "Delete Invoice"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default EditInvoicePage;
